import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { buildForbiddenPatterns, isCommandForbidden } from '../../utils/terminalSecurity';

const Terminal = forwardRef(({
  onCommandSubmit,
  response,
  connectionStabished,
  permission,
  forbiddenCommands,
  allowAllCommands = false,
  inputDisabled = false,
}, ref) => {
  const [commands, setCommands] = useState([]);
  const [input, setInput] = useState('');
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const terminalRef = useRef(null);

  // Compila lista final de padrões proibidos combinando padrão + prop opcional
  const forbiddenPatterns = useMemo(
    () => buildForbiddenPatterns(forbiddenCommands),
    [forbiddenCommands]
  );

  // Carregar histórico do localStorage na inicialização
  useEffect(() => {
    const savedHistory = localStorage.getItem('terminal-command-history');
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setCommandHistory(parsedHistory);
      } catch (error) {
        console.error('Erro ao carregar histórico de comandos:', error);
      }
    }
  }, []);

  // Salvar comando no histórico
  const saveCommandToHistory = (command) => {
    const newHistory = [...commandHistory];

    // Evitar comandos duplicados consecutivos
    if (newHistory[newHistory.length - 1] !== command) {
      newHistory.push(command);

      // Limitar histórico a 100 comandos
      if (newHistory.length > 100) {
        newHistory.shift();
      }

      setCommandHistory(newHistory);
      localStorage.setItem('terminal-command-history', JSON.stringify(newHistory));
    }

    // Resetar índice do histórico
    setHistoryIndex(-1);
  };

  // Navegação no histórico com setas
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex === -1
          ? commandHistory.length - 1
          : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIndex);
        setInput(commandHistory[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex >= 0) {
        const newIndex = historyIndex + 1;
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1);
          setInput('');
        } else {
          setHistoryIndex(newIndex);
          setInput(commandHistory[newIndex]);
        }
      }
    }
  };

  const submitCommand = (rawCommand, { bypassSecurity = false, saveHistory = true } = {}) => {
    const trimmedInput = String(rawCommand || '').trim();
    if (!trimmedInput) return false;

    setCommands((prevCommands) => [...prevCommands, { text: `> ${trimmedInput}`, type: 'command' }]);

    if (!bypassSecurity && !allowAllCommands && isCommandForbidden(trimmedInput, forbiddenPatterns)) {
      setCommands((prev) => [
        ...prev,
        { text: 'Comando bloqueado por segurança.', type: 'errorResponse' },
      ]);
      setShouldAutoScroll(true);
      return false;
    }

    if (saveHistory) {
      saveCommandToHistory(trimmedInput);
    }

    setShouldAutoScroll(true);
    if (onCommandSubmit) {
      onCommandSubmit(trimmedInput);
    }
    return true;
  };

  useImperativeHandle(ref, () => ({
    executeCommand: (command) => submitCommand(command, { bypassSecurity: true, saveHistory: false }),
  }), [allowAllCommands, forbiddenPatterns, onCommandSubmit]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputDisabled) return;
    if (input.trim()) {
      submitCommand(input);
      setInput('');
    }
  };

  // Função para verificar se o usuário está no final do scroll
  const isAtBottom = () => {
    if (!terminalRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = terminalRef.current;
    return scrollHeight - scrollTop - clientHeight < 10; // 10px de tolerância
  };

  // Função para lidar com o scroll
  const handleScroll = () => {
    if (terminalRef.current) {
      setShouldAutoScroll(isAtBottom());
    }
  };

  useEffect(() => {
    if (shouldAutoScroll && terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [commands, shouldAutoScroll]);

  useEffect(() => {
    if (response) {
      const responseType = response.startsWith('comando:') ? 'highlightedResponse' : 'normalResponse';
      setCommands((prevCommands) => [...prevCommands, { text: response, type: responseType }]);
    }
  }, [response]);

  return (
    <div style={styles.terminalContainer}>
      <div
        ref={terminalRef}
        style={styles.output}
        onScroll={handleScroll}
      >
        {commands.map((command, index) => (
          <div
            key={index}
            style={
              command.type === 'command'
                ? styles.command
                : command.type === 'highlightedResponse'
                  ? styles.highlightedResponse
                  : command.type === 'errorResponse'
                    ? styles.errorResponse
                    : styles.normalResponse
            }
          >
            {command.text}
          </div>
        ))}
      </div>
      <form
        onSubmit={handleSubmit}
        style={styles.form}
      >
        <input
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={inputDisabled ? 'Terminal bloqueado durante atualização...' : 'Digite um comando...'}
          style={{
            ...styles.input,
            opacity: inputDisabled ? 0.6 : 1,
            cursor: inputDisabled ? 'not-allowed' : 'text',
          }}
          type="text"
          value={input}
          disabled={!permission || inputDisabled}
        />
      </form>
    </div>
  );
});

Terminal.displayName = 'Terminal';

const styles = {
  terminalContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: '#1e1e1e',
    color: '#00ff00',
    borderRadius: '5px',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    fontSize: '14px',
  },
  output: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    marginBottom: '10px',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  command: {
    color: '#00ff00',
    margin: '2px 0',
  },
  highlightedResponse: {
    color: '#1E90FF',
    margin: '2px 0',
  },
  normalResponse: {
    color: '#FFFFFF',
    margin: '2px 0',
  },
  errorResponse: {
    color: '#FF5555',
    margin: '2px 0',
  },
  form: {
    display: 'flex',
  },
  input: {
    flex: 1,
    padding: '8px',
    borderRadius: '5px',
    border: '1px solid #555',
    backgroundColor: '#333',
    color: '#00ff00',
    outline: 'none',
  },
};

export default Terminal;
